import { 
    API_ENDPOINTS, 
    customToast, 
    DateFormats, 
    formatDateFns, 
    route, 
    Stepper, 
    StringHelpers, 
    SummaryLineItems, 
    useDisabledContext, 
    useLangContext, 
    Variants 
} from "carrier-fe";
import { useEffect, useMemo, useState } from "react";
import axios from 'axios';
import { useParams, useSearchParams } from "react-router-dom";
import { ToshibaInstallationSummary } from "../../types/installation";
import { MAX_TEXT_LENGTH, PRODUCT_LINES, STEPS } from "../../util/Constants";

function ToshibaStep7({
    loading
}: {
    loading?: boolean,
}) {
    const params = useParams();
    const [ searchParams ] = useSearchParams();
    const { disabled, setDisabled } = useDisabledContext();
    const { crud, fields } = useLangContext();
    const [ summaryData, setSummaryData ] = useState<ToshibaInstallationSummary>();

    const installationId =
        params?.installationId || searchParams?.get('installationId');

    useEffect(() => {
        if (!installationId) return;

        const fetchInstallation = async () => {
            setDisabled(true);
            try {
                const installation = await axios.get<{ data: ToshibaInstallationSummary }>(
                    route(
                        API_ENDPOINTS.REGISTRATION.INSTALLATION.EDIT, 
                        { 
                            installationId, 
                            step: String(STEPS.STEP_7) 
                        }
                    )
                );
    
                if (installation?.data?.data) 
                    setSummaryData(installation?.data?.data);

            } catch (error: any) {
                customToast({
                    title: error?.response?.data?.message,
                    variant: Variants.Danger,
                });
            } finally {
                setDisabled(false);
            }
        };

        fetchInstallation();
    }, [installationId]);

    const summaryConf = useMemo(() => {
        if (summaryData) return summaryConfig(summaryData, fields, crud);;
    }, [summaryData, fields, crud]);

    return (
        <Stepper.Step
            loading={disabled || loading}
            title={crud?.sub_titles?.summary || 'Summary'}
        >
            <div className="d-flex flex-column gap-3">
                {summaryConf?.map((item, index) => (
                    <SummaryLineItems
                        maxPerView={3}
                        key={index}
                        title={item.title}
                        items={item.items}
                    />
                ))}
            </div>
        </Stepper.Step>
    );
};

export default ToshibaStep7;

type SummaryItems = React.ComponentProps<typeof SummaryLineItems>['items'];

const summaryConfig = (
    installationData: ToshibaInstallationSummary,
    fields: any,
    crud: any,
): { title: string; items: SummaryItems }[] => {
    
    return [
        {
            title: crud?.sub_titles?.installer
                || 'Installer',
            items: [
                {
                    label: crud?.sub_titles?.company
                        || 'Company',
                    value: installationData?.installer_company_name
                        || '-'
                },
                {
                    label: crud?.models?.engineer
                        || 'Engineer',
                    value: installationData?.installer_individual_name
                        || '-'
                }
            ]
        },
        {
            title: crud?.sub_titles?.system
                || 'System',
            items: [
                {
                    label: StringHelpers.title(fields?.system_type
                        || 'system type'
                    ),
                    value: installationData?.product_parent_name
                        || '-'
                },
                {
                    label: StringHelpers.title(
                        `${crud?.sub_titles?.system || 'system'} ${fields?.configuration || 'configuration'}`
                    ),
                    value: installationData?.product_child_name
                        || '-'
                },
                ...installationData?.products ? 
                    installationData?.products?.map((product, index) => ({
                        label: `${crud?.models?.product
                            || 'Product'} ${index + 1}`,
                        value: [
                            {
                                label: crud?.models?.product_type
                                    || 'Product Type',
                                value: product.product_type 
                                    || '-'
                            },
                            {
                                label: crud?.models?.product_line
                                    || 'Product Line',
                                value: product.product_line 
                                    || '-'
                            },
                            {
                                label: fields?.model
                                    || 'Model',
                                value: product.product_model 
                                    || '-'
                            },
                            {
                                label: StringHelpers.title(
                                    fields?.serial_number
                                    || 'serial number'
                                ),
                                value: product.serial_number
                                    || '-'
                            },
                            {
                                label: `${crud?.models?.installation 
                                        || 'Installation'} ${
                                    fields?.location 
                                        || 'Location'
                                    }`,
                                value: product.installation_location
                                    || '-'
                            },
                            {
                                label: StringHelpers.title(
                                    fields?.water_circuit_description
                                    || 'Water circuit description'
                                ),
                                value: StringHelpers.limit(
                                    product.water_circuit_description
                                        || '-',
                                    MAX_TEXT_LENGTH
                                ),
                                visible: product.product_line === PRODUCT_LINES.CYLINDER
                            }
                        ]
                    })) 
                : [],
                {
                    label: StringHelpers.title(
                        fields?.total_system_refrigerant
                        || 'total system refrigerant'
                    ),
                    value: installationData?.total_system_refrigerant
                        || '-'
                },
            ],
        },
        {
            title: `${fields?.commissioning 
                    || 'Commissioning'}
                ${crud?.sub_titles?.details 
                    || 'Details'
                }`,
            items: [
                {
                    label: StringHelpers.title(
                        fields?.event_at
                        || 'date'
                    ),
                    value: formatDateFns(installationData?.commissioning_date, DateFormats.DATE)
                },
                {
                    label: StringHelpers.title(
                        fields?.distributor_ids 
                        || 'Distributors'
                    ),
                    value: installationData?.distributor_name
                        || '-'
                },
                {
                    label: StringHelpers.title(
                        crud?.customer 
                        || 'Customer'
                    ),
                    value: installationData?.customer_company_primary_individual_name
                        || '-'
                }
            ],
        },
        {
            title: crud?.models?.warranty
                || 'Warranty',
            items: [
                {
                    label: fields?.warranty_level
                        || 'Warranty level',
                    value: installationData?.company_warranty_level?.name
                        || '-',
                }
            ],
        }
    ];
};